import React, { useState, useEffect, useMemo, useReducer } from "react";
import {
  Toolbar,
  Divider,
  Typography,
  Avatar,
  Button,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Box,
  CssBaseline,
  Dialog,
  useMediaQuery,
  IconButton,
  Skeleton
} from "@mui/material";
import {
  Clear as ClearIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon
} from "@mui/icons-material";

import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { getAuth } from "firebase/auth";

import MainAppBar from "./MainAppBar";
import MainDrawer from "./MainDrawer";
import { createMenuItems } from "./MainDrawerMenu";
import { generateBreadcrumbs } from "./breadcrumbs";

const drawerWidth = 280;

// Initial state untuk menu yang bisa collapse
const initialMenuState = {
  location: false,
  labita: false,
  dms: false,
  pengamatan: false,
  ppic: false,
  plantation: false,
  boomspray: false,
  harvesting: false,
  production: false,
  masterdata: false
};

// Reducer untuk mengelola state menu
function menuReducer(state, action) {
  switch (action.type) {
    case 'TOGGLE':
      return {
        ...state,
        [action.menuName]: !state[action.menuName]
      };
    case 'SET_STATE':
      return {
        ...state,
        [action.menuName]: action.value
      };
    case 'RESET':
      return initialMenuState;
    default:
      return state;
  }
}

function Main({ user, userData, initialUserRole }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openAccountDialog, setOpenAccountDialog] = useState(false);
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  
  // Gunakan useReducer untuk mengelola state menu
  const [menuState, dispatch] = useReducer(menuReducer, initialMenuState);
  
  // Gunakan data yang sudah di-load dari App.js
  const [currentUser, setCurrentUser] = useState(userData);
  const [userRole, setUserRole] = useState(initialUserRole);

  // Update state ketika props berubah
  useEffect(() => {
    if (userData) {
      setCurrentUser(userData);
      setUserRole(userData.role);
    }
  }, [userData]);

  const activePath = location.pathname;

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  
  // Fungsi toggle yang fleksibel
  const handleToggleDrawerMenu = (menuName) => () => {
    dispatch({ type: 'TOGGLE', menuName });
  };

const handleLogout = () => {
  getAuth().signOut().then(() => {
    // Hapus semua cache user spesifik
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('user_cache_')) {
        localStorage.removeItem(key);
      }
    });
    localStorage.clear();
    navigate("/login");
  }).catch((error) => {
    console.error("Error logging out:", error);
  }).finally(() => {
    setOpenLogoutDialog(false);
  });
};

  const handleOpenLogoutDialog = () => {
    setOpenLogoutDialog(true);
    setOpenAccountDialog(false);
  };

  const handleCloseLogoutDialog = () => {
    setOpenLogoutDialog(false);
  };

  const handleAccountMenuClick = () => {
    setOpenAccountDialog(true);
  };

  const handleAccountMenuClose = () => {
    setOpenAccountDialog(false);
  };

  const handleMyProfileClick = () => {
    handleAccountMenuClose();
    navigate("/my-profile/profile");
  };

  const handleProfileSettingClick = () => {
    handleAccountMenuClose();
    navigate("/my-profile/setting");
  };

  // Generate menu items menggunakan fungsi terpisah
  const menuItems = useMemo(() => {
    return createMenuItems(
      userRole,
      menuState.dms,
      menuState.location,
      menuState.labita,
      menuState.pengamatan,
      menuState.ppic,
      menuState.plantation,
      menuState.boomspray,
      menuState.harvesting,
      menuState.production,
      menuState.masterdata,
      handleToggleDrawerMenu
    );
  }, [userRole, menuState]);

  // Generate breadcrumbs menggunakan fungsi terpisah
  const breadcrumbs = useMemo(() => {
    return generateBreadcrumbs(activePath, menuItems);
  }, [activePath, menuItems]);

  // Tampilkan skeleton loading jika data belum siap
  if (!userData) {
    return (
      <Box sx={{ display: "flex", height: "100vh" }}>
        <CssBaseline />
        
        {/* MainAppBar Skeleton */}
        <MainAppBar
          drawerWidth={drawerWidth}
          handleDrawerToggle={handleDrawerToggle}
          isMobile={isMobile}
          currentUser={null} // null untuk skeleton
          handleAccountMenuClick={() => {}}
          theme={theme}
        />
        
        {/* MainDrawer Skeleton - Mobile */}
        <Box 
          component="nav"
          sx={{ 
            width: { sm: drawerWidth }, 
            flexShrink: { sm: 0 },
            display: { xs: "none", sm: "block" } 
          }}
        >
          <Box
            sx={{
              width: drawerWidth,
              height: "100vh",
              borderRight: 1,
              borderColor: "divider",
              bgcolor: "background.paper"
            }}
          >
            {/* Drawer Header Skeleton */}
            <Toolbar>
              <Skeleton variant="text" width={120} height={32} />
            </Toolbar>
            <Divider />
            
            {/* Drawer Content Skeleton */}
            <Box sx={{ p: 2 }}>
              {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Skeleton variant="circular" width={24} height={24} sx={{ mr: 2 }} />
                  <Skeleton variant="text" width={`${Math.random() * 40 + 60}%`} height={20} />
                </Box>
              ))}
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Skeleton variant="text" width={80} height={16} sx={{ mb: 1 }} />
                {[1, 2, 3].map((i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", mb: 1.5, pl: 2 }}>
                    <Skeleton variant="circular" width={20} height={20} sx={{ mr: 2 }} />
                    <Skeleton variant="text" width={`${Math.random() * 30 + 50}%`} height={18} />
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Main Content Area Skeleton */}
        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1, 
            p: 3, 
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            mt: { xs: 7, sm: 0 } // Adjust for AppBar height
          }}
        >
          <Toolbar sx={{ minHeight: '64px', display: { sm: 'none' } }} /> {/* Spacer for mobile */}
          
          {/* Breadcrumbs Skeleton */}
          <Box sx={{ mb: 3 }}>
            <Skeleton variant="text" width={200} height={24} />
          </Box>
          
          {/* Page Title Skeleton */}
          <Skeleton variant="text" width="60%" height={40} sx={{ mb: 3 }} />
          
          {/* Content Skeleton */}
          <Box sx={{ display: "grid", gap: 3 }}>
            <Skeleton variant="rounded" width="100%" height={200} />
            <Skeleton variant="rounded" width="100%" height={150} />
            <Box sx={{ display: "flex", gap: 2 }}>
              <Skeleton variant="rounded" width="50%" height={100} />
              <Skeleton variant="rounded" width="50%" height={100} />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  // Render normal ketika data sudah siap
  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      <MainAppBar
        drawerWidth={drawerWidth}
        handleDrawerToggle={handleDrawerToggle}
        isMobile={isMobile}
        currentUser={currentUser}
        handleAccountMenuClick={handleAccountMenuClick}
        theme={theme}
      />

      <MainDrawer
        drawerWidth={drawerWidth}
        mobileOpen={mobileOpen}
        handleDrawerToggle={handleDrawerToggle}
        menuItems={menuItems}
        navigate={navigate}
        activePath={activePath}
      />

      <Box component="main" sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` } }}>
        <Toolbar sx={{ minHeight: '64px' }} />
        {/*<Outlet context={{ breadcrumbs }} />*/}
        <Outlet key={location.pathname} context={{ breadcrumbs }} />
      </Box>

      {/* Account Dialog */}
      <Dialog
        open={openAccountDialog}
        onClose={handleAccountMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 2,
            width: { xs: '90%', sm: '350px' },
            maxWidth: '350px',
            position: 'absolute',
            top: '80px',
            right: { xs: '5%', sm: '20px' },
            m: 0
          }
        }}
        BackdropProps={{ invisible: true }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <IconButton onClick={handleAccountMenuClose} size="small">
            <ClearIcon />
          </IconButton>
        </Box>
        {currentUser ? (
          <>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
              {currentUser.email}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
              <Avatar
                src={currentUser.avatar}
                sx={{ width: 60, height: 60, mb: 1, bgcolor: theme.palette.primary.main }}
              >
                {currentUser.name ? currentUser.name.charAt(0).toUpperCase() : ''}
              </Avatar>
              <Typography variant="h6" fontWeight="bold">
                Halo, {currentUser.name}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                sx={{ mt: 2, borderRadius: 50, textTransform: 'none' }}
                onClick={handleMyProfileClick}
              >
                My Profile
              </Button>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-around', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                sx={{ flex: 1, textTransform: 'none', borderRadius: 2 }}
                onClick={handleProfileSettingClick}
              >
                Profile Setting
              </Button>
              <Button
                variant="outlined"
                startIcon={<LogoutIcon />}
                sx={{ flex: 1, textTransform: 'none', borderRadius: 2 }}
                onClick={handleOpenLogoutDialog}
              >
                Logout
              </Button>
            </Box>
          </>
        ) : (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2 }}>Memuat data pengguna...</Typography>
          </Box>
        )}
      </Dialog>

      {/* Logout Dialog */}
      <Dialog
        open={openLogoutDialog}
        onClose={handleCloseLogoutDialog}
        aria-labelledby="logout-dialog-title"
      >
        <DialogTitle id="logout-dialog-title">Konfirmasi Logout</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Apakah Anda yakin ingin keluar dari aplikasi?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ ml: 2, mr: 2, pt: 0, pl: 0, pr: 0 }}>
          <Button onClick={handleCloseLogoutDialog}>Batal</Button>
          <Button onClick={handleLogout} autoFocus color="primary">
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Main;